/**
 * Canonical address type used across the application. All internal address
 * handling should use this shape. Normalize from external formats (Grid API,
 * sales drafts) using `normalizeAddress()`.
 */
export type Address = {
    addressId?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    city?: string;
    country?: string;
    countryCode?: string;
    street?: string;
    zipCode?: string;
};

export type ErpServiceAddress = {
    address_id?: string;
    city?: string;
    country_code?: string;
    country?: string;
    line1?: string;
    line2?: string;
    line3?: string;
    street?: string;
    zip?: string;
};

export type StarrocksAddress = {
    id?: string;
    address_line_1?: string | null;
    address_line_2?: string | null;
    address_line_3?: string | null;
    post_box?: string | null;
    zip_code_post_box?: string | null;
    street?: string | null;
    zip_code?: string | null;
    city?: string | null;
    country_code?: string | null;
    country?: string | null;
};

/**
 * Loose input type that accepts any known address shape.
 * All fields are optional so both snake_case and camelCase variants can be passed directly.
 */
export type AddressLike = Partial<
    Record<keyof Address | keyof ErpServiceAddress | keyof StarrocksAddress, string | null | undefined>
>;

/**
 * Normalizes any known address shape to the canonical Address type. Accepts
 * both snake_case (Grid API, sales drafts) and camelCase (CustomerAddressDTO)
 * formats.
 */
export function normalizeAddress(input: AddressLike): Address {
    return {
        addressId: input.addressId ?? input.address_id ?? input.id ?? undefined,
        addressLine1: input.addressLine1 ?? input.line1 ?? input.address_line_1 ?? undefined,
        addressLine2: input.addressLine2 ?? input.line2 ?? input.address_line_2 ?? undefined,
        addressLine3: input.addressLine3 ?? input.line3 ?? input.address_line_3 ?? undefined,
        street: input.street ?? undefined,
        zipCode: input.zipCode ?? input.zip ?? input.zip_code ?? undefined,
        city: input.city ?? undefined,
        country: input.country ?? undefined,
        countryCode: input.countryCode ?? input.country_code ?? undefined,
    };
}

export function toErpServiceAddress(input: AddressLike): ErpServiceAddress {
    const normalized = normalizeAddress(input);

    return {
        line1: normalized.addressLine1,
        line2: normalized.addressLine2,
        line3: normalized.addressLine3,
        city: normalized.city,
        country: normalized.country,
        country_code: normalized.countryCode,
        address_id: normalized.addressId,
        street: normalized.street,
        zip: normalized.zipCode,
    };
}

export function toStarrocksAddress(input: AddressLike): StarrocksAddress {
    const normalized = normalizeAddress(input);

    return {
        address_line_1: normalized.addressLine1,
        address_line_2: normalized.addressLine2,
        address_line_3: normalized.addressLine3,
        city: normalized.city,
        country: normalized.country,
        country_code: normalized.countryCode,
        id: normalized.addressId,
        street: normalized.street,
        zip_code: normalized.zipCode,
    };
}
